import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { ArrowLeft, Eye, KeyRound, Pencil, Plus, Search, UserCheck, UserX, X } from "lucide-react";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { fetchAccessScopeCatalog, fetchAccessScopes } from "./iamApi";
import { displayRoleName, initials, roleTone } from "./iamStudio";

const GUIDE_KEY = "iam_users_guide_dismissed";
const DEPT_TONES = ["sky", "violet", "amber", "teal", "rose"];

function Badge({ children, tone = "neutral" }) {
  return <span className={`iam-studio-badge ${tone}`}>{children}</span>;
}

function deptTone(name) {
  const text = String(name || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash + text.charCodeAt(i)) % DEPT_TONES.length;
  return DEPT_TONES[hash];
}

function formatWhen(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return `Today, ${time}`;
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday, ${time}`;
  return date.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
}

function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) items.push("…");
  for (let i = start; i <= end; i += 1) items.push(i);
  if (end < total - 1) items.push("…");
  items.push(total);
  return items;
}

export default function IamUsersStudio({
  users,
  roles,
  loading,
  saving,
  onCreateUser,
  onChangeRole,
  onSetStatus,
  onResetPassword,
  onSaveScope,
  onViewAccess,
}) {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [scopes, setScopes] = useState([]);
  const [catalog, setCatalog] = useState({ locations: [], departments: [] });
  const [drawer, setDrawer] = useState(null);
  // Add User is a full page (?view=add) inside the content area, like Add Leave Type.
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const outletCtx = useOutletContext();
  const setExtraCrumb = outletCtx?.setExtraCrumb;
  const addOpen = searchParams.get("view") === "add";
  const [formError, setFormError] = useState("");
  const [pending, setPending] = useState(null);
  const [empQuery, setEmpQuery] = useState("");
  const [showGuide, setShowGuide] = useState(() => localStorage.getItem(GUIDE_KEY) !== "1");

  useEffect(() => {
    fetchAccessScopes().then(setScopes).catch(() => setScopes([]));
    fetchAccessScopeCatalog().then(setCatalog).catch(() => {});
  }, [users]);

  useEffect(() => {
    if (!drawer || drawer.mode === "add") return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setDrawer(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawer]);

  const scopeByUser = useMemo(() => {
    const map = {};
    (scopes || []).forEach((row) => {
      map[row.user_id] = row;
    });
    return map;
  }, [scopes]);

  const accounts = useMemo(() => users.filter((u) => u.has_login && u.user_id), [users]);
  const departments = useMemo(
    () => [...new Set(accounts.map((u) => u.department).filter(Boolean))].sort(),
    [accounts]
  );
  const peopleWithoutLogin = useMemo(
    () => users.filter((u) => u.employee_id && !u.has_login),
    [users]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accounts.filter((u) => {
      const roleName = displayRoleName((u.roles || []).join(" ") || u.role || "");
      if (roleFilter && roleName !== roleFilter) return false;
      if (statusFilter && (u.status || "") !== statusFilter) return false;
      if (deptFilter && u.department !== deptFilter) return false;
      if (!q) return true;
      return [u.full_name, u.email, u.work_email, u.employee_code, u.department, u.branch, roleName]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [accounts, search, roleFilter, statusFilter, deptFilter]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pages);
  const rows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const filtersOn = Boolean(search || roleFilter || statusFilter || deptFilter);

  const newAddForm = () => {
    setFormError("");
    setEmpQuery("");
    setDrawer({
      mode: "add",
      employeeId: "",
      roleId: "",
      status: "active",
      scopeMode: "organisation",
      allLocations: true,
      locationIds: [],
      allDepartments: true,
      departmentIds: [],
      employeeScope: "all_employees",
    });
  };

  const openAdd = () => {
    newAddForm();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("view", "add");
        return next;
      },
      { state: { fromList: true } }
    );
  };

  const closeAdd = () => {
    if (location.state?.fromList) {
      navigate(-1);
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("view");
        return next;
      },
      { replace: true }
    );
  };

  // Keep the form in step with the URL (direct link, browser Back/Forward).
  useEffect(() => {
    if (addOpen && drawer?.mode !== "add") newAddForm();
    if (!addOpen && drawer?.mode === "add") setDrawer(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOpen]);

  // Breadcrumb: All Settings › Security › Users › Add User (also hides the page-level Back).
  useEffect(() => {
    if (!setExtraCrumb) return undefined;
    setExtraCrumb(addOpen ? "Add User" : null);
    return () => setExtraCrumb(null);
  }, [addOpen, setExtraCrumb]);

  const closeDrawer = () => {
    if (drawer?.mode === "add") closeAdd();
    else setDrawer(null);
  };

  const selectedEmployee = users.find((u) => String(u.employee_id) === String(drawer?.employeeId));
  const pickedRole = roles.find((r) => String(r.role_id) === String(drawer?.roleId));
  const employeeMatches = useMemo(() => {
    const q = empQuery.trim().toLowerCase();
    return peopleWithoutLogin.filter((u) => {
      if (!q) return true;
      return [u.full_name, u.employee_code, u.department, u.work_email, u.email]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    }).slice(0, 20);
  }, [peopleWithoutLogin, empQuery]);

  const visibleDepartments = useMemo(() => {
    const depts = catalog.departments || [];
    if (!drawer || drawer.allLocations) return depts;
    const selected = new Set(drawer.locationIds || []);
    return depts.filter((d) => d.shared || (d.location_ids || []).some((id) => selected.has(id)));
  }, [catalog, drawer]);

  const submitAdd = async () => {
    if (!drawer?.employeeId) {
      setFormError("Select an employee.");
      return;
    }
    if (!drawer.roleId) {
      setFormError("Assign a role. Permissions come from the role.");
      return;
    }
    const custom = drawer.scopeMode === "custom";
    if (custom && !drawer.allLocations && !drawer.locationIds.length) {
      setFormError("Select at least one location, or choose the whole organisation.");
      return;
    }
    if (custom && !drawer.allDepartments && !drawer.departmentIds.length) {
      setFormError("Select at least one department, or choose the whole organisation.");
      return;
    }
    const allLocations = custom ? drawer.allLocations : true;
    const allDepartments = custom ? drawer.allDepartments : true;
    const employeeScope = drawer.scopeMode === "reporting"
      ? "reporting_employees"
      : (custom ? drawer.employeeScope : "all_employees");
    setFormError("");
    try {
      const created = await onCreateUser({
        employee_id: drawer.employeeId,
        role_ids: [drawer.roleId],
        primary_role_id: drawer.roleId,
        create_login_if_missing: true,
      });
      if (!created?.user_id) return;
      await onSaveScope(created.user_id, {
        all_locations: allLocations,
        location_ids: allLocations ? [] : drawer.locationIds,
        all_departments: allDepartments,
        department_ids: allDepartments ? [] : drawer.departmentIds,
        employee_scope: employeeScope,
      });
      if (drawer.status === "inactive") await onSetStatus(created.user_id, "deactivate");
      closeAdd();
    } catch (err) {
      setFormError(err?.response?.data?.detail || err?.message || "Could not create the user");
    }
  };

  const submitRole = async () => {
    if (!drawer?.roleId) {
      setFormError("Select a role.");
      return;
    }
    setFormError("");
    try {
      await onChangeRole({
        user_id: drawer.user.user_id,
        employee_id: drawer.user.employee_id,
        role_ids: [drawer.roleId],
        primary_role_id: drawer.roleId,
        create_login_if_missing: false,
      });
      setDrawer(null);
    } catch (err) {
      setFormError(err?.response?.data?.detail || err?.message || "Could not update the role");
    }
  };

  const dismissGuide = () => {
    localStorage.setItem(GUIDE_KEY, "1");
    setShowGuide(false);
  };

  const clearFilters = () => {
    setSearch("");
    setRoleFilter("");
    setStatusFilter("");
    setDeptFilter("");
    setPage(1);
  };

  return (
    <div className={`iam-ux ${drawer ? "with-drawer" : ""}`}>
      {!addOpen && (
      <div className="iam-ux-main">
        <header className="iam-ux-head">
          <div>
            <h2>Users</h2>
            <p>Manage users, assign roles, and control who can sign in.</p>
          </div>
          <span className="iam-ux-count">{filtered.length} {filtered.length === 1 ? "user" : "users"}</span>
        </header>

        {showGuide && (
          <div className="iam-ux-guide">
            <article>
              <span>1</span>
              <div>
                <strong>Pick an employee</strong>
                <p>Add User only lists people who do not have a login yet.</p>
              </div>
            </article>
            <article>
              <span>2</span>
              <div>
                <strong>Assign one role</strong>
                <p>The role decides view, add, edit, approve, and export.</p>
              </div>
            </article>
            <article>
              <span>3</span>
              <div>
                <strong>Choose a scope</strong>
                <p>Whole organisation is the simplest start. Narrow it later.</p>
              </div>
            </article>
            <button type="button" onClick={dismissGuide}>Got it</button>
          </div>
        )}

        <div className="iam-ux-toolbar">
          <label className="iam-ux-search">
            <Search size={16} />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by name, employee ID or email"
            />
          </label>
          <select className="iam-ux-select" value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}>
            <option value="">Role: All</option>
            {roles.map((r) => (
              <option key={r.role_id} value={displayRoleName(r.name)}>{displayRoleName(r.name)}</option>
            ))}
          </select>
          <select className="iam-ux-select" value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setPage(1); }}>
            <option value="">Department: All</option>
            {departments.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <select className="iam-ux-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">Status: All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          {filtersOn && (
            <button type="button" className="iam-ux-clear" onClick={clearFilters}>Clear filters</button>
          )}
          <button type="button" className="iam-ux-add" onClick={openAdd}>
            <Plus size={16} /> Add User
          </button>
        </div>

        <div className="iam-ux-card">
          {loading ? <p className="iam-ux-empty">Loading users…</p> : (
            <table className="iam-ux-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Employee ID</th>
                  <th>Department</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const rawRole = (u.roles || [])[0] || u.role || "—";
                  const roleName = displayRoleName(rawRole);
                  const scope = scopeByUser[u.user_id];
                  return (
                    <tr key={u.row_id}>
                      <td>
                        <div className="iam-studio-person">
                          <span className={`iam-studio-avatar ${roleTone(roleName)}`}>{initials(u.full_name)}</span>
                          <span>
                            <strong>{u.full_name || "—"}</strong>
                            <small>{u.email || u.work_email || "—"}</small>
                          </span>
                        </div>
                      </td>
                      <td className="iam-ux-mono">{u.employee_code || "—"}</td>
                      <td>
                        {u.department ? (
                          <span className={`iam-ux-dept ${deptTone(u.department)}`}>{u.department}</span>
                        ) : "—"}
                      </td>
                      <td><Badge tone={roleTone(roleName)}>{roleName}</Badge></td>
                      <td>
                        <span className={`iam-ux-status ${u.status === "active" ? "on" : "off"}`}>
                          <i />
                          {u.status === "active" ? "Active" : (u.status || "—")}
                        </span>
                      </td>
                      <td className="iam-ux-when">{formatWhen(u.last_login)}</td>
                      <td>
                        <div className="iam-ux-actions">
                          <button type="button" title="View user" onClick={() => { setFormError(""); setDrawer({ mode: "view", user: u, scope }); }}>
                            <Eye size={14} /> View
                          </button>
                          <button
                            type="button"
                            title="Change role"
                            onClick={() => {
                              setFormError("");
                              const current = roles.find((r) => (u.role_ids || []).includes(r.role_id) || r.name === rawRole || displayRoleName(r.name) === roleName);
                              setDrawer({ mode: "edit", user: u, roleId: current?.role_id || "" });
                            }}
                          >
                            <Pencil size={14} /> Edit
                          </button>
                          <button type="button" title="Reset password" onClick={() => setPending({ kind: "password", user: u })}>
                            <KeyRound size={14} /> Reset
                          </button>
                          <button
                            type="button"
                            className={u.status === "active" ? "danger" : ""}
                            title={u.status === "active" ? "Deactivate" : "Activate"}
                            onClick={() => setPending({
                              kind: "status",
                              user: u,
                              action: u.status === "active" ? "deactivate" : "activate",
                            })}
                          >
                            {u.status === "active" ? <UserX size={14} /> : <UserCheck size={14} />}
                            {u.status === "active" ? "Deactivate" : "Activate"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!loading && !accounts.length && (
            <div className="iam-ux-zero">
              <strong>No one can sign in yet</strong>
              <p>Add a user from your employee list, give them one role, and they can log in.</p>
              <button type="button" className="iam-ux-add" onClick={openAdd}><Plus size={16} /> Add User</button>
            </div>
          )}
          {!loading && accounts.length > 0 && !rows.length && (
            <p className="iam-ux-empty">No users match these filters. <button type="button" onClick={clearFilters}>Clear filters</button></p>
          )}
          <div className="iam-ux-pager">
            <span>
              Showing {rows.length ? (safePage - 1) * pageSize + 1 : 0}–{(safePage - 1) * pageSize + rows.length} of {filtered.length}
            </span>
            <div className="iam-ux-pages">
              <button type="button" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
              {pageWindow(safePage, pages).map((item, index) => (
                item === "…" ? <span key={`gap-${index}`}>…</span> : (
                  <button
                    key={item}
                    type="button"
                    className={item === safePage ? "on" : ""}
                    onClick={() => setPage(item)}
                  >
                    {item}
                  </button>
                )
              ))}
              <button type="button" disabled={safePage >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
            <label className="iam-ux-pagesize">
              Rows
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </label>
          </div>
        </div>
      </div>
      )}

      {drawer && (() => {
        const isPage = drawer.mode === "add";
        const panel = (
        <aside
          className={`iam-ux-drawer ${isPage ? "large iam-ux-addpage" : ""}`}
          role={isPage ? undefined : "dialog"}
          aria-modal={isPage ? undefined : "true"}
        >
          <header>
            <div>
              <h3>
                {drawer.mode === "add" && "Add User"}
                {drawer.mode === "edit" && "Change Role"}
                {drawer.mode === "view" && (drawer.user?.full_name || "User")}
              </h3>
              <p>
                {drawer.mode === "add" && "Three steps: employee, role, then who they can see."}
                {drawer.mode === "edit" && "The role decides what this person can do."}
                {drawer.mode === "view" && "Account, role, and current access scope."}
              </p>
            </div>
            {!isPage && (
              <button type="button" className="iam-studio-icon" onClick={closeDrawer} aria-label="Close">
                <X size={16} />
              </button>
            )}
          </header>
          <div className="iam-ux-drawer-body">
            {drawer.mode === "view" && (
              <>
                <dl className="iam-studio-facts">
                  <div><dt>Employee ID</dt><dd>{drawer.user.employee_code || "—"}</dd></div>
                  <div><dt>Department</dt><dd>{drawer.user.department || "—"}</dd></div>
                  <div><dt>Location</dt><dd>{drawer.user.branch || "—"}</dd></div>
                  <div><dt>Role</dt><dd>{displayRoleName((drawer.user.roles || [])[0] || drawer.user.role || "—")}</dd></div>
                  <div><dt>Status</dt><dd>{drawer.user.status || "—"}</dd></div>
                  <div><dt>Last login</dt><dd>{formatWhen(drawer.user.last_login)}</dd></div>
                  <div><dt>Access scope</dt><dd>{drawer.scope?.summary || "Not configured"}</dd></div>
                </dl>
              </>
            )}

            {drawer.mode === "edit" && (
              <section className="iam-ux-section">
                <h4>Role assignment</h4>
                <label className="iam-studio-field">
                  Role
                  <select value={drawer.roleId} onChange={(e) => setDrawer((d) => ({ ...d, roleId: e.target.value }))}>
                    <option value="">Select role</option>
                    {roles.map((r) => (
                      <option key={r.role_id} value={r.role_id}>{displayRoleName(r.name)}</option>
                    ))}
                  </select>
                </label>
                {pickedRole && (
                  <div className="iam-ux-hint">
                    <strong>{displayRoleName(pickedRole.name)}</strong>
                    <p>{pickedRole.description || "Permissions from this role apply automatically. Edit them on the Roles page."}</p>
                  </div>
                )}
              </section>
            )}

            {drawer.mode === "add" && (
              <div className="iam-ux-add-grid">
                <section className="iam-ux-section iam-ux-section-pick">
                  <h4>Employee</h4>
                  <label className="iam-studio-field">
                    Search employee
                    <input
                      value={empQuery}
                      onChange={(e) => setEmpQuery(e.target.value)}
                      placeholder="Name, ID, or department"
                    />
                  </label>
                  <div className="iam-ux-picklist">
                    {employeeMatches.map((u) => (
                      <button
                        key={u.employee_id}
                        type="button"
                        className={String(drawer.employeeId) === String(u.employee_id) ? "on" : ""}
                        onClick={() => setDrawer((d) => ({ ...d, employeeId: u.employee_id }))}
                      >
                        <span className="iam-studio-avatar">{initials(u.full_name)}</span>
                        <span>
                          <strong>{u.full_name}</strong>
                          <small>{[u.employee_code, u.department, u.work_email || u.email].filter(Boolean).join(" · ") || "Employee"}</small>
                        </span>
                      </button>
                    ))}
                    {!employeeMatches.length && (
                      <p>{peopleWithoutLogin.length ? "No employee matches that search." : "Every employee already has a login."}</p>
                    )}
                  </div>
                </section>

                <div className="iam-ux-add-side">
                <section className="iam-ux-section">
                  <h4>Selected person</h4>
                  {selectedEmployee ? (
                    <div className="iam-ux-picked">
                      <span className="iam-studio-avatar">{initials(selectedEmployee.full_name)}</span>
                      <div>
                        <strong>{selectedEmployee.full_name}</strong>
                        <p>{selectedEmployee.employee_code || "No employee ID"} · {selectedEmployee.department || "No department"}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="iam-ux-note">Pick someone on the left. Their name, ID, email, and department stay here.</p>
                  )}
                  <div className="iam-ux-readonly">
                    <label>Employee ID<input readOnly value={selectedEmployee?.employee_code || ""} placeholder="Filled when you pick someone" /></label>
                    <label>Email<input readOnly value={selectedEmployee?.work_email || selectedEmployee?.email || ""} placeholder="Filled when you pick someone" /></label>
                    <label>Department<input readOnly value={selectedEmployee?.department || ""} placeholder="Filled when you pick someone" /></label>
                    <label>Location<input readOnly value={selectedEmployee?.branch || ""} placeholder="Filled when you pick someone" /></label>
                  </div>
                  <label className="iam-studio-field">
                    Status
                    <select value={drawer.status} onChange={(e) => setDrawer((d) => ({ ...d, status: e.target.value }))}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </label>
                </section>

                <section className="iam-ux-section">
                  <h4>Role</h4>
                  <label className="iam-studio-field">
                    Assign role
                    <select value={drawer.roleId} onChange={(e) => setDrawer((d) => ({ ...d, roleId: e.target.value }))}>
                      <option value="">Select role</option>
                      {roles.map((r) => (
                        <option key={r.role_id} value={r.role_id}>{displayRoleName(r.name)}</option>
                      ))}
                    </select>
                    <small>Permissions are copied from this role. Change them on Roles, not here.</small>
                  </label>
                  {pickedRole ? (
                    <div className="iam-ux-hint">
                      <strong>{displayRoleName(pickedRole.name)}</strong>
                      <p>{pickedRole.description || "This role’s module permissions will apply as soon as the user is created."}</p>
                    </div>
                  ) : (
                    <div className="iam-ux-hint muted">
                      <strong>What a role does</strong>
                      <p>It answers what they can open. It does not limit branch or department — that is the scope below.</p>
                    </div>
                  )}
                </section>

                <section className="iam-ux-section">
                  <h4>Access scope</h4>
                  <label className="iam-studio-field">
                    Who can they see?
                    <select
                      value={drawer.scopeMode}
                      onChange={(e) => setDrawer((d) => ({ ...d, scopeMode: e.target.value }))}
                    >
                      <option value="organisation">Whole organisation</option>
                      <option value="reporting">Reporting employees only</option>
                      <option value="custom">Selected locations and departments</option>
                    </select>
                  </label>
                  {drawer.scopeMode === "organisation" && (
                    <p className="iam-ux-note">Simplest choice. They can see every location and department.</p>
                  )}
                  {drawer.scopeMode === "reporting" && (
                    <p className="iam-ux-note">They only see people who report to them.</p>
                  )}
                  {drawer.scopeMode === "custom" && (
                    <>
                      <label className="iam-studio-checkline">
                        <input
                          type="checkbox"
                          checked={drawer.allLocations}
                          onChange={(e) => setDrawer((d) => ({ ...d, allLocations: e.target.checked, locationIds: [] }))}
                        />
                        All locations
                      </label>
                      {!drawer.allLocations && (
                        <div className="iam-studio-checks">
                          {(catalog.locations || []).map((loc) => (
                            <label key={loc.location_id}>
                              <input
                                type="checkbox"
                                checked={drawer.locationIds.includes(loc.location_id)}
                                onChange={(e) => setDrawer((d) => ({
                                  ...d,
                                  locationIds: e.target.checked
                                    ? [...d.locationIds, loc.location_id]
                                    : d.locationIds.filter((id) => id !== loc.location_id),
                                  departmentIds: [],
                                }))}
                              />
                              {loc.name}
                            </label>
                          ))}
                        </div>
                      )}
                      <label className="iam-studio-checkline">
                        <input
                          type="checkbox"
                          checked={drawer.allDepartments}
                          onChange={(e) => setDrawer((d) => ({ ...d, allDepartments: e.target.checked, departmentIds: [] }))}
                        />
                        All departments
                      </label>
                      {!drawer.allDepartments && (
                        <div className="iam-studio-checks">
                          {visibleDepartments.map((dept) => (
                            <label key={dept.department_id}>
                              <input
                                type="checkbox"
                                checked={drawer.departmentIds.includes(dept.department_id)}
                                onChange={(e) => setDrawer((d) => ({
                                  ...d,
                                  departmentIds: e.target.checked
                                    ? [...d.departmentIds, dept.department_id]
                                    : d.departmentIds.filter((id) => id !== dept.department_id),
                                }))}
                              />
                              {dept.name}
                            </label>
                          ))}
                        </div>
                      )}
                      <label className="iam-studio-field">
                        Inside that selection
                        <select
                          value={drawer.employeeScope}
                          onChange={(e) => setDrawer((d) => ({ ...d, employeeScope: e.target.value }))}
                        >
                          <option value="all_employees">All employees</option>
                          <option value="reporting_employees">Reporting employees only</option>
                        </select>
                      </label>
                    </>
                  )}
                </section>
                </div>
              </div>
            )}
            {formError && <p className="iam-studio-error">{formError}</p>}
          </div>
          <footer>
            <button type="button" className="iam-studio-btn" onClick={closeDrawer}>Cancel</button>
            {drawer.mode === "view" && (
              <button type="button" className="iam-studio-btn primary" onClick={() => onViewAccess(drawer.user.user_id)}>
                Open access scope
              </button>
            )}
            {drawer.mode === "edit" && (
              <button type="button" className="iam-studio-btn primary" disabled={saving} onClick={submitRole}>
                {saving ? "Saving…" : "Save role"}
              </button>
            )}
            {drawer.mode === "add" && (
              <button type="button" className="iam-studio-btn primary" disabled={saving} onClick={submitAdd}>
                {saving ? "Creating…" : "Create User"}
              </button>
            )}
          </footer>
        </aside>
        );
        if (isPage) {
          return (
            <div className="iam-ux-addpage-wrap">
              <button type="button" className="iam-ux-backlink" onClick={closeAdd}>
                <ArrowLeft size={15} /> Back to Users
              </button>
              {panel}
            </div>
          );
        }
        // Pop-ups (View / Change Role) render on <body>, centred above the sidebar.
        return createPortal(
          <div className="iam-ux-portal">
            <button type="button" className="iam-ux-backdrop" aria-label="Close panel" onClick={closeDrawer} />
            {panel}
          </div>,
          document.body
        );
      })()}
      <ConfirmDialog
        open={Boolean(pending)}
        danger={pending?.action === "deactivate" || pending?.kind === "password"}
        title={pending?.kind === "password" ? "Send password reset?" : pending?.action === "deactivate" ? "Deactivate user?" : "Activate user?"}
        message={
          pending?.kind === "password"
            ? `Send a reset link to ${pending.user.email || pending.user.full_name}.`
            : `${pending?.action === "deactivate" ? "Deactivate" : "Activate"} ${pending?.user?.full_name || "this user"}? Their role and access scope stay saved.`
        }
        confirmLabel={pending?.kind === "password" ? "Send reset link" : "Confirm"}
        onCancel={() => setPending(null)}
        onConfirm={async () => {
          const action = pending;
          setPending(null);
          if (!action) return;
          if (action.kind === "password") await onResetPassword(action.user);
          else await onSetStatus(action.user.user_id, action.action);
        }}
      />
    </div>
  );
}
